import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import DownloadIcon from '@icons/DownloadIcon';
import SubtractIconWithBadge from '@icons/SubtractIconWithBadge';
import XIcon from '@icons/XIcon';
import { setupForDark } from '@theme';
import TextInput from './TextInput';

interface AttachmentCardProps {
  // === Core Data ===
  filename: string;

  // === Optional Content ===
  text?: string | null; // Description/comment text
  isEditingText?: boolean; // true = TextInput, false = readonly display
  textPlaceholder?: string;
  textMaxLength?: number;
  allowRemove?: boolean;
  textDisplayMode?: 'borderless' | 'default' | 'editor';
  textClassName?: string;
  // === Progress ===
  progress?: number | null; // null = hide, 0-100 = show with percentage

  // === Actions ===
  onRemove?: () => void;
  onDownload?: () => void;
  onTextChange?: (text: string) => void;
  // === Styling ===
  className?: string;
  iconSize?: number; // Default: 40

  cardMaxWidth?: string; // e.g., 'sm:max-w-[500px]'
}

export default function AttachmentCard({
  filename,
  text,
  isEditingText = false,
  textPlaceholder,
  textMaxLength = 1000,
  progress = null,
  allowRemove = true,
  textDisplayMode = 'borderless',
  textClassName = '',
  onRemove,
  onDownload,
  onTextChange,
  className,
  iconSize = 40,
  cardMaxWidth
}: AttachmentCardProps) {
  const { t } = useTranslation();

  // Extract file extension if not provided
  const getDisplayedFileType = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex !== -1
      ? filename
          .substring(lastDotIndex + 1)
          .toUpperCase()
          .slice(0, 5)
      : t('components.attachmentCard.defaultFileType');
  };

  const displayedFileType = getDisplayedFileType(filename);
  const displayFilename = filename || t('components.attachmentCard.unnamedFile');

  // Determine if we show text row
  const showTextRow = text !== undefined && text !== null && (text || isEditingText);

  // Determine if we show progress row
  const showProgressRow = progress !== null && progress !== undefined;

  return (
    <div
      className={classNames(
        'w-full flex flex-col gap-xs p-sm  rounded-xs border',
        setupForDark('bg-secondary', 'border-gray-300', 'text-primary'),
        cardMaxWidth,
        className
      )}
    >
      {/* Row 1: Icon, Filename, Action Button(s) */}
      <div className={classNames('w-full flex flex-row items-center gap-sm')}>
        {/* File Icon */}
        <div className={classNames('flex flex-row items-center pr-md')}>
          <SubtractIconWithBadge
            text={displayedFileType}
            iconSize={iconSize}
            className={classNames(setupForDark('text-icon-primary'))}
          />
        </div>

        {/* Filename */}
        <div className={classNames('P2B flex-1 min-w-0 truncate')}>{displayFilename}</div>

        {/* Action Buttons */}
        {onDownload && (
          <button
            type='button'
            onClick={onDownload}
            className={classNames(
              'cursor-pointer flex-shrink-0',
              setupForDark('text-icon-primary')
            )}
          >
            <DownloadIcon size={25} />
          </button>
        )}

        {onRemove && allowRemove && (
          <div onClick={onRemove} className='cursor-pointer flex-shrink-0'>
            <XIcon size={24} className={setupForDark('text-icon-primary')} />
          </div>
        )}
      </div>

      {/* Row 2: Text/Description (Optional) */}
      {showTextRow && (
        <div className={classNames('flex flex-col gap-xs')}>
          {isEditingText ? (
            <TextInput
              displayMode={textDisplayMode}
              placeholder={textPlaceholder || t('components.attachmentCard.textPlaceholder')}
              value={text || ''}
              maxLength={textMaxLength}
              textClassName={textClassName}
              onChange={onTextChange}
            />
          ) : (
            <div className={classNames('P2R text-wrap')}>{text || textPlaceholder}</div>
          )}
        </div>
      )}

      {/* Row 3: Progress Bar (Optional) */}
      {showProgressRow && (
        <div
          className={classNames(
            'w-full h-xs rounded-sm overflow-hidden',
            setupForDark('bg-gray-200')
          )}
        >
          <div
            className={classNames(
              'h-full rounded-sm transition-all duration-300 ease-out',
              setupForDark('bg-violet-700')
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}
